import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaLinkedin, FaEnvelope } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Spinner from '../Spinner/Spinner';
import './Team.css';

// Default team members (fallback)
const defaultTeamMembers = [
  {
    id: 'jc-lhotellier',
    name: 'Jean-Christophe Lhotellier',
    title_en: 'Co-Founder & CEO',
    title_fr: 'Co-Fondateur & PDG',
    imageUrl: '/assets/images/person-professional-1.jpg',
    linkedin: '#',
    email: 'jc.lhotellier@opteam-services.com'
  },
  {
    id: 'r-coquet',
    name: 'Rémi Coquet',
    title_en: 'Co-Founder & CTO',
    title_fr: 'Co-Fondateur & CTO',
    imageUrl: '/assets/images/person-professional-2.jpg',
    linkedin: '#',
    email: 'r.coquet@opteam-services.com'
  }
];

const Team = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === 'fr' ? 'fr' : 'en';
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Helper function to get localized content
  const getLocalizedContent = (item, field) => {
    const localizedField = `${field}_${currentLang}`;
    const fallbackField = `${field}_en`;
    return item[localizedField] || item[fallbackField] || item[field] || '';
  };

  useEffect(() => {
    const fetchFounders = async () => {
      try {
        const q = query(collection(db, 'founders'), orderBy('order', 'asc'));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const items = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setTeamMembers(items);
        } else {
          setTeamMembers(defaultTeamMembers);
        }
      } catch (err) {
        console.error('Error fetching founders:', err);
        setTeamMembers(defaultTeamMembers);
      } finally {
        setLoading(false);
      }
    };

    fetchFounders();
  }, []);

  if (loading) {
    return (
      <section id="team" className="team" ref={ref}>
        <div className="container">
          <Spinner size="medium" text={t('team.loading')} />
        </div>
      </section>
    );
  }

  return (
    <section id="team" className="team" ref={ref}>
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {t('team.title')}
        </motion.h2>

        <div className="team-grid">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.id}
              className="team-member"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <div className="member-photo">
                <img
                  src={member.imageUrl || member.image}
                  alt={member.name}
                  loading="lazy"
                  decoding="async"
                />
                <div className="member-overlay">
                  <div className="member-links">
                    {member.linkedin && (
                      <a href={member.linkedin} className="social-link" target="_blank" rel="noopener noreferrer">
                        <FaLinkedin />
                      </a>
                    )}
                    {member.email && (
                      <a href={`mailto:${member.email}`} className="social-link">
                        <FaEnvelope />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="member-info">
                <h3>{member.name}</h3>
                <p className="member-title">{getLocalizedContent(member, 'title')}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
